import type { EvaluationPlanCategoryBundle, EvaluationPlanWorkspaceDraft } from '../types/evaluationPlan';
import { PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY } from '../constants/adminLearningContentScope';
import { buildEvaluationPlanBundleVersion } from './evaluationPlanCacheVersion';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import {
  idbDeleteEvaluationPlanBundle,
  idbGetEvaluationPlanBundle,
  idbPutEvaluationPlanBundle,
} from './evaluationPlanBundleIdb';

/** v3: contentVersion(head) 비교용 메타 저장 */
const BUNDLE_CACHE_VERSION = 'v3';
const CATEGORIES_CACHE_VERSION = 'v3';

function bundleStorageKey(categoryName: string): string {
  const name = categoryName.trim();
  return `cp.adminEvaluationPlan.bundle.${BUNDLE_CACHE_VERSION}.${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}.${encodeURIComponent(name)}`;
}

function categoriesStorageKey(): string {
  return `cp.adminEvaluationPlan.categories.${CATEGORIES_CACHE_VERSION}.${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}`;
}

function normalizeBundle(raw: unknown): EvaluationPlanCategoryBundle | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Partial<EvaluationPlanCategoryBundle>;
  if (typeof obj.categoryName !== 'string') return null;
  return {
    categoryName: obj.categoryName,
    standards: Array.isArray(obj.standards) ? obj.standards : [],
    plans: Array.isArray(obj.plans) ? obj.plans : [],
  };
}

/**
 * 관리자 번들 캐시: 메모리(동기 조회) ← IndexedDB(영속, 대용량). 예전 LocalStorage 항목은 읽을 때 IDB 로 이관 후 삭제.
 * 서비스는 조회 전에 hydrateAdminEvaluationPlanBundleCache 를 await 해 IDB 값을 메모리로 올린다.
 */
const memBundles = new Map<string, { bundle: EvaluationPlanCategoryBundle; contentVersion: string }>();

function idbKey(name: string): string {
  return `admin:${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}:${name}`;
}

export async function hydrateAdminEvaluationPlanBundleCache(categoryName: string): Promise<void> {
  const name = categoryName.trim();
  if (!name || memBundles.has(name)) return;
  const entry = await idbGetEvaluationPlanBundle(idbKey(name));
  if (entry && !memBundles.has(name)) {
    memBundles.set(name, { bundle: { ...entry.bundle, categoryName: name }, contentVersion: entry.contentVersion });
  }
}

export function loadAdminEvaluationPlanBundleCache(
  categoryName: string
): { found: boolean; bundle: EvaluationPlanCategoryBundle | null; contentVersion: string | null } {
  const name = categoryName.trim();
  if (!name || typeof window === 'undefined') {
    return { found: false, bundle: null, contentVersion: null };
  }
  const mem = memBundles.get(name);
  if (mem) return { found: true, bundle: mem.bundle, contentVersion: mem.contentVersion };
  try {
    const raw = window.localStorage.getItem(bundleStorageKey(name));
    if (!raw) return { found: false, bundle: null, contentVersion: null };
    const parsed = JSON.parse(raw) as { bundle?: unknown; contentVersion?: string };
    const bundle = normalizeBundle(parsed?.bundle ?? parsed);
    if (!bundle) return { found: false, bundle: null, contentVersion: null };
    const stored =
      typeof parsed?.contentVersion === 'string' ? parsed.contentVersion.trim() : '';
    const result = {
      found: true,
      bundle: { ...bundle, categoryName: name },
      contentVersion: stored || buildEvaluationPlanBundleVersion({ ...bundle, categoryName: name }),
    };
    /** 레거시 LS → IDB 이관 */
    saveAdminEvaluationPlanBundleCache(name, result.bundle, { contentVersion: result.contentVersion });
    return result;
  } catch {
    return { found: false, bundle: null, contentVersion: null };
  }
}

export function saveAdminEvaluationPlanBundleCache(
  categoryName: string,
  bundle: EvaluationPlanCategoryBundle,
  options?: { contentVersion?: string }
): void {
  const name = categoryName.trim();
  if (!name || typeof window === 'undefined') return;
  const next = { ...bundle, categoryName: name };
  const contentVersion =
    (options?.contentVersion ?? '').trim() || buildEvaluationPlanBundleVersion(next);
  memBundles.set(name, { bundle: next, contentVersion });
  void idbPutEvaluationPlanBundle(idbKey(name), { bundle: next, contentVersion, savedAt: Date.now() });
  try {
    window.localStorage.removeItem(bundleStorageKey(name));
  } catch {
    /* noop */
  }
}

export function clearAdminEvaluationPlanBundleCache(categoryName: string): void {
  const name = categoryName.trim();
  if (!name || typeof window === 'undefined') return;
  memBundles.delete(name);
  void idbDeleteEvaluationPlanBundle(idbKey(name));
  try {
    window.localStorage.removeItem(bundleStorageKey(name));
  } catch {
    /* noop */
  }
}

export function patchAdminEvaluationPlanBundleCache(
  categoryName: string,
  patch: (prev: EvaluationPlanCategoryBundle) => EvaluationPlanCategoryBundle
): void {
  const name = categoryName.trim();
  if (!name || typeof window === 'undefined') return;
  const cached = loadAdminEvaluationPlanBundleCache(name);
  if (!cached.found || !cached.bundle) return;
  saveAdminEvaluationPlanBundleCache(name, patch({ ...cached.bundle, categoryName: name }));
}

export function renameAdminEvaluationPlanBundleCache(fromName: string, toName: string): void {
  const from = fromName.trim();
  const to = toName.trim();
  if (!from || !to || from === to || typeof window === 'undefined') return;
  const cached = loadAdminEvaluationPlanBundleCache(from);
  if (!cached.found || !cached.bundle) {
    clearAdminEvaluationPlanBundleCache(from);
    return;
  }
  saveAdminEvaluationPlanBundleCache(
    to,
    { ...cached.bundle, categoryName: to },
    { contentVersion: cached.contentVersion ?? undefined }
  );
  clearAdminEvaluationPlanBundleCache(from);
}

export type CachedEvaluationPlanCategory = {
  id: string;
  category_name: string;
  sort_order: number;
  level_step_count: number;
  level_label_a?: string;
  level_label_b?: string;
  level_label_c?: string;
  level_label_d?: string;
  level_label_e?: string;
  updated_at?: string;
};

export function loadAdminEvaluationPlanCategoriesCache(): {
  found: boolean;
  categories: CachedEvaluationPlanCategory[];
  contentVersion: string | null;
} {
  if (typeof window === 'undefined') return { found: false, categories: [], contentVersion: null };
  try {
    const raw = window.localStorage.getItem(categoriesStorageKey());
    if (!raw) return { found: false, categories: [], contentVersion: null };
    const parsed = JSON.parse(raw) as { categories?: unknown; contentVersion?: string };
    if (!Array.isArray(parsed.categories)) return { found: false, categories: [], contentVersion: null };
    const categories = parsed.categories
      .filter(
        (row): row is CachedEvaluationPlanCategory =>
          !!row &&
          typeof row === 'object' &&
          typeof (row as CachedEvaluationPlanCategory).category_name === 'string'
      )
      .map((row) => ({
        ...row,
        level_step_count: typeof row.level_step_count === 'number' ? row.level_step_count : 5,
      }));
    const stored =
      typeof parsed.contentVersion === 'string' ? parsed.contentVersion.trim() : '';
    return {
      found: true,
      categories,
      contentVersion: stored || buildLearningContentScopeVersion(categories),
    };
  } catch {
    return { found: false, categories: [], contentVersion: null };
  }
}

export function saveAdminEvaluationPlanCategoriesCache(
  categories: CachedEvaluationPlanCategory[],
  options?: { contentVersion?: string }
): void {
  if (typeof window === 'undefined') return;
  try {
    const contentVersion =
      (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(categories);
    window.localStorage.setItem(
      categoriesStorageKey(),
      JSON.stringify({ categories, contentVersion, savedAt: Date.now() })
    );
  } catch {
    /* noop */
  }
}

export function patchAdminEvaluationPlanCategoriesCache(
  patch: (prev: CachedEvaluationPlanCategory[]) => CachedEvaluationPlanCategory[]
): void {
  const cached = loadAdminEvaluationPlanCategoriesCache();
  const next = patch(cached.found ? cached.categories : []);
  saveAdminEvaluationPlanCategoriesCache(next);
}

const WORKSPACE_SESSION_VERSION = 'v1';

function workspaceSessionStorageKey(): string {
  return `cp.adminEvaluationPlan.workspaceSession.${WORKSPACE_SESSION_VERSION}.${PLATFORM_ADMIN_LEARNING_CONTENT_CACHE_KEY}`;
}

export type EvaluationPlanWorkspaceSessionEntry = {
  draft: EvaluationPlanWorkspaceDraft;
  baselineJson: string;
};

export type EvaluationPlanWorkspaceSessionState = {
  draftsByCategory: Record<string, EvaluationPlanWorkspaceSessionEntry>;
  updatedAt: number;
};

function normalizeWorkspaceSessionEntry(raw: unknown): EvaluationPlanWorkspaceSessionEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Partial<EvaluationPlanWorkspaceSessionEntry>;
  if (!obj.draft || typeof obj.draft !== 'object' || typeof obj.baselineJson !== 'string') return null;
  const draft = obj.draft as EvaluationPlanWorkspaceDraft;
  if (typeof draft.categoryName !== 'string') return null;
  return {
    draft: {
      categoryName: draft.categoryName,
      standards: Array.isArray(draft.standards) ? draft.standards : [],
      plans: Array.isArray(draft.plans) ? draft.plans : [],
      deletedStandardIds: Array.isArray(draft.deletedStandardIds) ? draft.deletedStandardIds : [],
      deletedPlanIds: Array.isArray(draft.deletedPlanIds) ? draft.deletedPlanIds : [],
    },
    baselineJson: obj.baselineJson,
  };
}

export function loadEvaluationPlanWorkspaceSessionState(): EvaluationPlanWorkspaceSessionState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(workspaceSessionStorageKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EvaluationPlanWorkspaceSessionState>;
    if (!parsed.draftsByCategory || typeof parsed.draftsByCategory !== 'object') return null;
    const draftsByCategory: Record<string, EvaluationPlanWorkspaceSessionEntry> = {};
    for (const [name, entry] of Object.entries(parsed.draftsByCategory)) {
      const normalized = normalizeWorkspaceSessionEntry(entry);
      if (normalized) draftsByCategory[name.trim()] = normalized;
    }
    if (!Object.keys(draftsByCategory).length) return null;
    return {
      draftsByCategory,
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function saveEvaluationPlanWorkspaceSessionEntry(
  categoryName: string,
  entry: EvaluationPlanWorkspaceSessionEntry
): void {
  const name = categoryName.trim();
  if (!name || typeof window === 'undefined') return;
  try {
    const prev = loadEvaluationPlanWorkspaceSessionState();
    const draftsByCategory = { ...(prev?.draftsByCategory ?? {}), [name]: entry };
    window.sessionStorage.setItem(
      workspaceSessionStorageKey(),
      JSON.stringify({ draftsByCategory, updatedAt: Date.now() } satisfies EvaluationPlanWorkspaceSessionState)
    );
  } catch {
    /* noop */
  }
}

export function clearEvaluationPlanWorkspaceSessionEntry(categoryName: string): void {
  const name = categoryName.trim();
  if (!name || typeof window === 'undefined') return;
  try {
    const prev = loadEvaluationPlanWorkspaceSessionState();
    if (!prev?.draftsByCategory[name]) return;
    const { [name]: _removed, ...rest } = prev.draftsByCategory;
    if (!Object.keys(rest).length) {
      window.sessionStorage.removeItem(workspaceSessionStorageKey());
      return;
    }
    window.sessionStorage.setItem(
      workspaceSessionStorageKey(),
      JSON.stringify({ draftsByCategory: rest, updatedAt: Date.now() } satisfies EvaluationPlanWorkspaceSessionState)
    );
  } catch {
    /* noop */
  }
}
